import React, { ReactElement, ReactNode } from 'react'
import uniqueId from 'lodash/uniqueId'
import styled from 'styled-components'

import { COLORS, COLORS2, SIZES } from '~/common/globalStyles'
import Icon from '~/views/components/ui/Icon'
import { Label } from '~/views/components/ui/inputs/v2/shared/components/inputElements'
import Tooltip from '~/views/components/ui/Tooltip'

import HelpTooltip from '../../../Tooltip/HelpTooltip'
import MultiSelect from './components/MultiSelect'
import SearchableMultiSelect from './components/SearchableMultiSelect'
import SearchableSelect from './components/SearchableSelect'
import SingleSelect from './components/SingleSelect'

/**
 *
 *
 * @class Select
 * @desc This is just a wrapper around the single/multi select components to include the label and make it easier to import and use 1 component
 */

const SelectContainer = styled.div<{ minWidth?: string }>`
  &.OperatorOptions > div > div[type='button'] {
    background: none;
    min-height: 1px;
    min-width: ${({ minWidth }) => (minWidth ? minWidth : SIZES.ultra)};
    padding: 0px ${SIZES.tiny} 0px ${SIZES.xxs};
    align-items: center;
    justify-content: space-around;
  }

  &.OperatorOptions > div[role='combobox'] {
    display: flex;
    align-items: center;
    min-height: ${SIZES.small};
  }

  &.OperatorOptions > div > div[role='listbox'] {
    min-width: ${SIZES.sideNavOpen};
    right: 0;
  }
`

interface GroupedArrayType {
  title: string
  data: ReactElement[]
}

export interface SelectProps {
  label?: string | ReactNode
  searchable?: boolean
  multi?: boolean
  required?: boolean
  disabled?: boolean
  helperText?: React.ReactNode | string
  field?: string
  tooltipText?: string | ReactElement
  externalSync?: boolean
  style?: React.CSSProperties
  noPadding?: boolean
  floating?: boolean
  className?: string
  minWidth?: string
  status?: string | null
  statusLabel?: React.ReactNode
  value?: string | number | null
  name?: string
  menuWrapper?: boolean
  fullWidth?: boolean
  placeholder?: string
  children?: React.ReactNode
  dataTestId?: string
  onChange?: (selected: any) => void
  onBlur?: (event: React.FocusEvent<Element>) => void
  renderValue?:
    | (({ value }: SelectProps) => JSX.Element)
    | ((values: string[]) => void)
  filterMethod?: (child: React.ReactElement, inputValue: string) => any
  grouped?: boolean
  groupCriteria?:
    | ((data: React.ReactElement[]) => GroupedArrayType[])
    | ((data: React.ReactElement[]) => void)
}

class Select extends React.Component<SelectProps> {
  id = `input_${uniqueId()}`

  override render() {
    const {
      label,
      searchable,
      multi,
      required,
      disabled,
      helperText,
      tooltipText,
      externalSync,
      style,
      className,
      minWidth,
      status,
      statusLabel,
      placeholder,
      dataTestId,
      ...rest
    } = this.props

    // We currently can't have a  searchable multiselect. Multi-will trump for now if both are given
    const SelectComponent = multi
      ? searchable
        ? SearchableMultiSelect
        : MultiSelect
      : searchable
        ? SearchableSelect
        : SingleSelect

    return (
      <SelectContainer minWidth={minWidth} className={className}>
        {label && (
          <div>
            <Label required={required} htmlFor={this.id}>
              {label}
              {status === 'warn' && (
                <Tooltip title={statusLabel} placement="top-start">
                  <Icon
                    name="alert1"
                    height="15px"
                    style={{ marginLeft: '8px' }}
                    color={COLORS.yellow2}
                  />
                </Tooltip>
              )}
            </Label>
            {tooltipText && <HelpTooltip title={tooltipText} />}
          </div>
        )}
        <SelectComponent
          dataTestId={dataTestId}
          externalSync={externalSync}
          {...rest}
          required={required}
          disabled={disabled}
          placeholder={placeholder}
          style={style}
          id={this.id}
        />
        {helperText && (
          <div style={{ marginTop: SIZES.xs, color: COLORS2.gray['300'] }}>
            {helperText}
          </div>
        )}
      </SelectContainer>
    )
  }
}

export default Select
